import React from "react";

import { useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { getProcessOverviewData, getCompanyPolicy } from "EmployerApplication/Selectors";
import {
  choosePlanURL,
  renewalCoverageOptionsURL,
  renewalOpenEnrollmentURL,
  renewalDocumentUploadURL,
  renewalEmployeeStatusURL,
  renewalPaymentInformationURL,
  renewalCompanyInformationURL,
  renewalOverviewURL,
} from "EmployerApplication/urls";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { companyTokenString } from "Utils/companyToken";
import { baseUrl as basename } from "ZeroRuby/urls";

import { BodyMedium } from "Cobalt/BodyText";
import { Step, Steps } from "Cobalt/Steps";
import { appendAuthParams } from "Common/TokenAuth/Utils";

type Props = {
  overview: "past" | "present" | "future";
  choosePlan: "past" | "present" | "future";
  companyInformation: "past" | "present" | "future";
  coverageOptions: "past" | "present" | "future";
  openEnrollment: "past" | "present" | "future";
  documentUpload: "past" | "present" | "future";
  changingCarrier: boolean;
  openEnrollmentCompleted: boolean;
};

// Needed for pages that don't live in the router, since they won't
// get the basename appended by the router.
const rubyPagePrefix = () => `${basename}${companyTokenString()}`;

const isStepCompleted = (status: Props["overview"]) => status !== "future";

const stepLink = (label: string, status: Props["overview"], options: { to?: string; href?: string }) => {
  const labelWeight = status === "present" ? "bold" : "normal";

  if (status === "future") {
    return (
      <BodyMedium color="muted" weight={labelWeight}>
        {label}
      </BodyMedium>
    );
  }

  if (options.to) {
    return <BodyMedium weight={labelWeight}>{React.createElement(Link as React.ElementType, { to: options.to }, label)}</BodyMedium>;
  }

  return (
    <BodyMedium weight={labelWeight}>
      <a href={options.href}>{label}</a>
    </BodyMedium>
  );
};

const ProcessOverviewDumb = ({
  overview,
  choosePlan,
  coverageOptions,
  openEnrollment,
  documentUpload,
  changingCarrier,
  openEnrollmentCompleted,
  companyInformation,
}: Props) => {
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const needBankingInfo = changingCarrier && companyApplication && !companyApplication.bankingInfoPresent;
  return (
    <Steps>
      <Step completed={isStepCompleted(overview)}>
        {stepLink("Renewal Overview", overview, { href: appendAuthParams(rubyPagePrefix() + renewalOverviewURL) })}
      </Step>

      <Step completed={isStepCompleted(choosePlan)}>{stepLink("Choose Plan", choosePlan, { href: rubyPagePrefix() + choosePlanURL })}</Step>

      <Step completed={isStepCompleted(companyInformation)}>{stepLink("Company Information", companyInformation, { to: renewalCompanyInformationURL })}</Step>

      <Step completed={isStepCompleted(coverageOptions)}>{stepLink("Confirm Selection", coverageOptions, { to: renewalCoverageOptionsURL })}</Step>

      <Step completed={isStepCompleted(openEnrollment)}>
        {stepLink("Open Enrollment", openEnrollment, { to: openEnrollmentCompleted ? renewalEmployeeStatusURL : renewalOpenEnrollmentURL })}
      </Step>

      {changingCarrier ? (
        <Step completed={isStepCompleted(documentUpload)}>{stepLink("Upload Documents", documentUpload, { to: renewalDocumentUploadURL })}</Step>
      ) : null}
      {changingCarrier && needBankingInfo ? (
        <Step completed={false}>{stepLink("Payment Information", "future", { to: renewalPaymentInformationURL })}</Step>
      ) : null}
    </Steps>
  );
};

const stepStatus = (location: string, url: string, completed: boolean): "past" | "present" | "future" => {
  if (location === url) {
    return "present";
  }
  if (completed) {
    return "past";
  }
  return "future";
};

const mapStateToProps = (state, ownProps) => {
  const companyPolicy = getCompanyPolicy(state);
  const data = getProcessOverviewData(state);
  const location = ownProps.history.location.pathname;
  return {
    overview: stepStatus(location, renewalOverviewURL, data.overview),
    choosePlan: stepStatus(location, choosePlanURL, data.choosePlan),
    companyInformation: stepStatus(location, renewalCompanyInformationURL, data.companyInformation),
    coverageOptions: stepStatus(location, renewalCoverageOptionsURL, data.coverageOptions),

    openEnrollment: stepStatus(location, renewalOpenEnrollmentURL, data.openEnrollment),

    openEnrollmentCompleted: data.openEnrollment,
    employeeStatusCompleted: data.employeeStatus,
    documentUpload: stepStatus(location, renewalDocumentUploadURL, data.documentUpload),

    changingCarrier: companyPolicy ? companyPolicy.changingCarrier : null,
  };
};

export default withRouter(connect(mapStateToProps)(ProcessOverviewDumb));
